import * as Sentry from '@sentry/react';
import { ErrorState } from '@/components/ui/error-state';

/**
 * The last net: a render error no component caught.
 *
 * React unmounts the whole tree when a render throws, so without this what is
 * left is a blank screen — the worst possible report, because the person has
 * nothing to tell and we receive nothing. `Sentry.ErrorBoundary` does the two
 * missing things: it sends the error with the component stack, and it paints
 * something that explains what happened and offers a retry.
 *
 * Fetch errors do NOT reach here (RTK Query resolves them inside its hooks and
 * each screen paints them with this very `ErrorState`); `sentryErrorMiddleware`
 * takes care of those. What reaches here is the programming defect: an
 * `undefined` read as an object, a `map` over something that was not a list.
 */
export function AppErrorBoundary({ children }: { children: React.ReactNode }): React.JSX.Element {
  return (
    <Sentry.ErrorBoundary
      // `resetError` remounts the tree. It deliberately does not reload the
      // page: a reload loses the state of a half-filled form, and the typical
      // case — unexpected data in a list — is fixed by a remount.
      fallback={({ error, resetError }) => (
        <div className="flex min-h-screen items-center justify-center p-6">
          <ErrorState
            error={error}
            title="Algo se rompió en esta pantalla"
            onRetry={resetError}
          />
        </div>
      )}
      showDialog={false}
    >
      {children}
    </Sentry.ErrorBoundary>
  );
}
